import { Button } from 'antd';
import { CheckOutlined, GiftOutlined } from '@ant-design/icons';
import GiftImage from 'src/_shared/assets/snappy_present.png';
import {
	GIFT_STATE,
	PIN_STATE,
	formatClaimBy,
	isGiftOutstanding,
	isGiftResolved,
	isPinResolved,
} from 'src/_shared/components/milestone-gift-modal/milestoneClaims.js';
import './style.scss';

const yearsPrefix = (claim, suffix) =>
	claim.years ? `${claim.years}-${suffix}` : '';

export const MilestoneGiftBanner = ({ claim, onClaimGift, isProcessing }) => {
	const claimBy = formatClaimBy(claim.gift.claimBy);

	return (
		<div className="milestone-gift-banner">
			<div className="milestone-gift-banner-image">
				<img src={GiftImage} alt="Anniversary gift" />
			</div>
			<div className="milestone-gift-banner-text">
				<span className="milestone-gift-banner-title">
					Your {yearsPrefix(claim, 'year ')}anniversary gift is waiting
				</span>
				<span className="milestone-gift-banner-subtitle">
					This is separate from your service pin.
					{claimBy ? ` Choose your gift by ${claimBy}.` : ''}
				</span>
			</div>
			<Button
				type="primary"
				shape="round"
				size="large"
				loading={isProcessing}
				onClick={() => onClaimGift(claim)}
			>
				{claim.gift.state === GIFT_STATE.STARTED
					? 'Finish Claiming Gift'
					: 'Claim Gift'}
			</Button>
		</div>
	);
};

const ClaimedLabel = ({ children = 'Claimed' }) => (
	<span className="milestone-reward-row-done">
		<CheckOutlined /> {children}
	</span>
);

const ClaimLink = ({ children, onClick, isProcessing }) => (
	<Button
		type="link"
		size="large"
		icon={<GiftOutlined />}
		loading={isProcessing}
		disabled={isProcessing}
		onClick={onClick}
		className="milestone-reward-row-link"
	>
		<strong style={{ marginLeft: '4px' }}>
			{isProcessing ? 'Processing...' : children}
		</strong>
	</Button>
);

export const MilestoneRewardGroup = ({
	claim,
	onClaimPin,
	onClaimGift,
	processingKeys = new Set(),
}) => {
	const { pin, gift } = claim;
	const resolvedCount =
		(isPinResolved(claim) ? 1 : 0) + (isGiftResolved(claim) ? 1 : 0);
	const pinClaimBy = formatClaimBy(pin.claimBy);
	const giftClaimBy = formatClaimBy(gift.claimBy);

	const renderPinAction = () => {
		switch (pin.state) {
			case PIN_STATE.UNCLAIMED:
				return (
					<ClaimLink
						onClick={() => onClaimPin(claim)}
						isProcessing={processingKeys.has(`${claim.id}:pin`)}
					>
						Claim Reward
					</ClaimLink>
				);
			case PIN_STATE.STARTED:
				return (
					<ClaimLink onClick={() => onClaimPin(claim)}>
						Finish Claiming Reward
					</ClaimLink>
				);
			case PIN_STATE.CLAIMED:
				return <ClaimedLabel />;
			case PIN_STATE.DECLINED:
				return <span className="milestone-reward-row-muted">Declined</span>;
			default:
				return <span className="milestone-reward-row-muted">Expired</span>;
		}
	};

	const renderGiftAction = () => {
		if (isGiftOutstanding(claim)) {
			return (
				<ClaimLink
					onClick={() => onClaimGift(claim)}
					isProcessing={processingKeys.has(`${claim.id}:gift`)}
				>
					{gift.state === GIFT_STATE.STARTED
						? 'Finish Claiming Reward'
						: 'Claim Reward'}
				</ClaimLink>
			);
		}

		switch (gift.state) {
			case GIFT_STATE.CLAIMED:
				return <ClaimedLabel />;
			case GIFT_STATE.EXPIRED:
				return <span className="milestone-reward-row-muted">Expired</span>;
			default:
				return gift.link ? (
					<ClaimLink onClick={() => onClaimGift(claim)}>
						Open in Snappy
					</ClaimLink>
				) : null;
		}
	};

	return (
		<div className="milestone-reward-group">
			<div className="milestone-reward-group-header">
				<span className="milestone-reward-group-title">
					{claim.years
						? `${claim.years}-Year Service Anniversary`
						: claim.badgeName || 'Service Anniversary'}
				</span>
				<span className="milestone-reward-group-count">
					{resolvedCount} of 2 claimed
				</span>
			</div>
			<div className="milestone-reward-row">
				<div className="milestone-reward-row-image is-round">
					{pin.image ? (
						<img src={pin.image} alt={pin.reward?.name || 'Service pin'} />
					) : null}
				</div>
				<span className="milestone-reward-row-name">
					Service pin
					{pin.state === PIN_STATE.STARTED && (
						<span className="milestone-reward-row-meta"> · Started</span>
					)}
					{pin.state === PIN_STATE.UNCLAIMED && pinClaimBy && (
						<span className="milestone-reward-row-meta">
							{' '}
							· claim by {pinClaimBy}
						</span>
					)}
				</span>
				{renderPinAction()}
			</div>
			<div className="milestone-reward-row">
				<div className="milestone-reward-row-image">
					<img src={GiftImage} alt="Anniversary gift" />
				</div>
				<span className="milestone-reward-row-name">
					Anniversary gift
					{isGiftOutstanding(claim) && giftClaimBy && (
						<span className="milestone-reward-row-meta">
							{' '}
							· claim by {giftClaimBy}
						</span>
					)}
				</span>
				{renderGiftAction()}
			</div>
		</div>
	);
};
